import { type CSSProperties, useState } from 'react';
import { ColorPicker, Dialog } from '@vibe/core';
import { Check, DropdownChevronDown } from '@vibe/icons';
import { type PortalSettings } from '@gorilla/portal-shared/src/schemas';

type Swatch = { color: string; label: string };

// Ordered by color tone, with neutral colors last.
export const TEASER_SWATCHES: Swatch[] = [
  { color: '#a0472b', label: 'Rust' },
  { color: '#8a5a12', label: 'Ochre' },
  { color: '#fdab3d', label: 'Warning' },
  { color: '#5c6b2e', label: 'Olive' },
  { color: '#3d6b4f', label: 'Forest' },
  { color: '#00c875', label: 'Success' },
  { color: '#2f5d62', label: 'Teal' },
  { color: '#1f6f8b', label: 'Ocean' },
  { color: '#2c4a6e', label: 'Navy' },
  { color: '#494c9e', label: 'Violet' },
  { color: '#574663', label: 'Mulberry' },
  { color: '#9c3d5a', label: 'Berry' },
  { color: '#df2f4a', label: 'Error' },
  { color: '#465464', label: 'Slate' },
  { color: '#2b2f36', label: 'Charcoal' },
];

// Set on an element around the swatches, so that a swatch without a color previews the portal's theme.
export const getSwatchThemeStyle = (theme: PortalSettings['theme']) =>
  ({ '--swatchpicker--primary': theme.primaryColor, '--swatchpicker--secondary': theme.secondaryColor }) as CSSProperties;

type SwatchSettingProps = {
  color?: string;
  swatches?: Swatch[];
  onChange: (newColor: string | undefined) => void;
};

export function SwatchSetting({ color, swatches = TEASER_SWATCHES, onChange }: SwatchSettingProps) {
  const [open, setOpen] = useState(false);

  const normalizedColor = color?.trim().toLowerCase();
  const selected = swatches.find((swatch) => swatch.color === normalizedColor);
  // Shown on hover. A value saved before the swatches existed still renders in the portal, so call it custom rather
  // than hiding it.
  const label = !normalizedColor ? 'Default' : (selected?.label ?? 'Custom');

  const select = (newColor: string | undefined) => {
    onChange(newColor);
    setOpen(false);
  };

  return (
    <Dialog
      open={open}
      showTrigger={[]}
      hideTrigger={[]}
      onClickOutside={() => setOpen(false)}
      position="bottom-end"
      moveBy={{ main: 4 }}
      content={
        // ColorPicker brings its own dialog container. forceUseRawColorList renders our hex values as-is instead of
        // filtering them against monday's named colors; hover names are only supported for those, so there are none here.
        <div
          onKeyDown={(event) => {
            if (event.key === 'Escape') setOpen(false);
          }}
        >
          <ColorPicker
            forceUseRawColorList
            colorsList={swatches.map((swatch) => swatch.color)}
            value={selected?.color}
            numberOfColorsInLine={5}
            colorSize="medium"
            noColorText={normalizedColor ? 'Reset to default color' : undefined}
            SelectedIndicatorIcon={Check}
            className="swatchpicker__colors"
            onSave={(colors) => select(colors?.[0])}
          />
        </div>
      }
    >
      <button
        type="button"
        className="swatchpicker__trigger"
        title={label}
        aria-label={`Background color: ${label}`}
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => setOpen(true)}
      >
        <span
          className="swatchpicker__dot"
          style={normalizedColor ? { backgroundColor: normalizedColor, color: '#fcfefe' } : undefined}
          aria-hidden="true"
        >
          A
        </span>
        <DropdownChevronDown className="swatchpicker__chevron" />
      </button>
    </Dialog>
  );
}
